import React, { useEffect, useState } from 'react';
import {
  Button,
  Container,
  Copy,
  Flex,
  Heading,
  Link,
  Loader,
  Radio,
  Stack,
  SvgX,
  UtilityBanner,
} from '@grubhubprod/gh-cookbook-react';
import { Section, Square } from '@grubhubprod/gh-cookbook-react/dist';
import classnames from 'classnames';
import { useForm } from 'react-hook-form/dist/index.ie11';
import { useParams } from 'react-router-dom';
import { SignupStepFooter } from 'src/components/common/features/Signup/components/SignupStepFooter/SignupStepFooter';
import { useEnterpriseSignupStatus } from 'src/components/EnterpriseSignupStatusProvider/EnterpriseSignupStatusProvider';
import { EnterpriseBackToHomeButton } from 'src/components/EnterpriseBackToHomeButton/EnterpriseBackToHomeButton';
import { EnterpriseCardForm } from 'src/components/EnterpriseSignupSteps/EnterpriseBillingStep/EnterpriseCardForm';
import { sdk } from 'src/utilities/sdk';
import {
  pushError,
  pushErrorFromAPI,
  pushSuccess,
} from 'src/utilities/notifications';
import { useAuth } from 'src/components/AuthProvider/AuthProvider';
import { decamelizeRequestBody } from 'src/utilities/http-data-casing';
import { SimpleDialog } from 'src/components/SimpleDialog/SimpleDialog';
import { useAnalytics } from 'src/components/AnalyticsProvider/AnalyticsProvider';

interface ICard {
  id: string;
  create_date: number;
  credit_card_last4: string;
  credit_card_type: string;
  expiration_month: number;
  expiration_year: number;
  expired: boolean;
}
export function EnterpriseBillingStep(): JSX.Element {
  const { goToPreviousStep, goToNextStep, canManageCreditCard } =
    useEnterpriseSignupStatus();
  const { corpid } = useParams<{ corpid: string }>();

  const {
    enterpriseData: { isInternalAdminUser, isOnlyCorpAdminUser },
    corpData,
  } = useAuth();
  const [card, setCard] = useState<ICard>(null);
  const [showRemoveCardModal, setShowRemoveCardModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { register, watch, setValue, handleSubmit } = useForm({
    mode: 'onChange',
    defaultValues: {
      creditCardAutopay: corpData.creditCardAutopay,
    },
  });
  const { sendTealiumPageView } = useAnalytics();
  const hasCreditCard = !!card;
  const creditCardAutopay = watch('creditCardAutopay');

  useEffect(() => {
    sendTealiumPageView({
      pagegroup: 'corporate admin - signup flow',
      pagesubgroup: 'billing details',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (corpid) {
      getCreditCard();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [corpid]);

  async function getCreditCard(): Promise<void> {
    if (isOnlyCorpAdminUser) return;
    try {
      let paymentApiClientTokenUrl = `/payments/corporate/${corpid}/credit_card`;

      if (isInternalAdminUser) {
        paymentApiClientTokenUrl = `${paymentApiClientTokenUrl}/internal_admin`;
      }

      setIsLoading(true);
      const [card] = await sdk.http.get(paymentApiClientTokenUrl);
      setCard(card);
    } catch (error) {
      pushErrorFromAPI(error);
    } finally {
      setIsLoading(false);
    }
  }

  function getExpDateFromCard(month: string, year: string): string {
    return `${month.length < 2 ? `0${month}` : month}/${year.substring(2, 4)}`;
  }

  async function removeCard(): Promise<void> {
    try {
      await sdk.http.delete(
        `/payments/corporate/${corpid}/payments/${card.id}`
      );

      pushSuccess('Your card has been removed.');
      setCard(null);
      setShowRemoveCardModal(false);
    } catch (e) {
      pushError('There was a problem removing your card. Try again.');
    }
  }

  async function onAddCardSuccess(): Promise<void> {
    await getCreditCard();
    pushSuccess('Card has been added');
  }

  async function onAddCardError(): Promise<void> {
    pushError('Card was not added. Try again.');
  }

  async function onFormSubmit(values): Promise<void> {
    try {
      await sdk.http.put(
        `/corps/${corpid}`,
        decamelizeRequestBody({
          ...corpData,
          creditCardAutopay: values.creditCardAutopay,
        })
      );
      goToNextStep();
    } catch (e) {
      pushErrorFromAPI(e);
    }
  }

  return (
    <>
      <Section inset="lg">
        <Container variant="md">
          <Stack y="x-sm">
            <Flex justifyContent="space-between">
              <Heading variant="h3">Finally, set up your billing</Heading>
              <EnterpriseBackToHomeButton />
            </Flex>
          </Stack>
          <Stack y="md">
            <Copy variant="body">
              As a last step, you need a payment option for your meal credits.
              Once this is done, you can access statements from your corporate
              admin portal at any time.
            </Copy>
          </Stack>
          {(hasCreditCard || canManageCreditCard) && (
            <>
              <Stack y="x-sm">
                <Heading variant="h6">Credit card details</Heading>
              </Stack>
              <Stack y="sm">
                Pay online with a credit card or reach out to{' '}
                <Link href="mailto:clients@grubhub.com">
                  clients@grubhub.com
                </Link>{' '}
                if you want to pay by ACH, check, wire transfer, or by location
                and departments.
              </Stack>
            </>
          )}
          <Stack y="md">
            {isLoading ? (
              <Flex justifyContent="center">
                <Loader size="md" inverted />
              </Flex>
            ) : (
              <>
                {hasCreditCard ? (
                  <Square
                    inset="md"
                    dimension={canManageCreditCard ? '1' : null}
                    border={canManageCreditCard ? '0' : '1px solid'}
                    borderColor="lightLine"
                    borderRadius="4px"
                    data-testid="card-banner"
                  >
                    <Flex alignItems="bottom">
                      <Stack
                        x="x-sm"
                        className={classnames('creditCardIcon', {
                          [card.credit_card_type]: true,
                        })}
                      />
                      <Stack x="x-sm">
                        <Flex alignItems="center">
                          <Stack
                            style={{ textTransform: 'capitalize' }}
                            x="xxx-sm"
                          >
                            {card.credit_card_type}
                          </Stack>
                          ending in {card.credit_card_last4}, exp{' '}
                          {getExpDateFromCard(
                            card.expiration_month.toString(),
                            card.expiration_year.toString()
                          )}
                        </Flex>
                      </Stack>
                      {canManageCreditCard && (
                        <Button
                          variant="tertiary"
                          style={{ display: 'flex', alignItems: 'bottom' }}
                          className="tableActionButtons"
                          onClick={(): void => setShowRemoveCardModal(true)}
                        >
                          <SvgX aria-label="Remove" />
                        </Button>
                      )}
                    </Flex>
                  </Square>
                ) : (
                  canManageCreditCard && (
                    <EnterpriseCardForm
                      corpId={corpid as string}
                      onError={onAddCardError}
                      onSuccess={onAddCardSuccess}
                    />
                  )
                )}
              </>
            )}
          </Stack>
          {((!canManageCreditCard && hasCreditCard) || canManageCreditCard) && (
            <Stack y={canManageCreditCard ? 'none' : 'md'}>
              <Stack y="x-sm">
                <Heading variant="h6">Invoice payment</Heading>
              </Stack>
              <form>
                <Stack y="sm">
                  <Copy variant="body">
                    How do you want to pay with this credit card?
                  </Copy>
                </Stack>
                <Flex alignItems="center">
                  <Stack x="md">
                    <Radio
                      id="pay-manually"
                      name="creditCardAutopay"
                      value="OFF"
                      ref={register}
                      disabled={!canManageCreditCard}
                      checked={creditCardAutopay === 'OFF'}
                      onChange={() => setValue('creditCardAutopay', 'OFF')}
                    >
                      Pay manually
                    </Radio>
                  </Stack>
                  <Radio
                    id="pay-automatically"
                    name="creditCardAutopay"
                    value="ON"
                    ref={register}
                    disabled={!canManageCreditCard}
                    checked={creditCardAutopay === 'ON'}
                    onChange={(): void => setValue('creditCardAutopay', 'ON')}
                  >
                    Pay automatically
                  </Radio>
                </Flex>
              </form>
            </Stack>
          )}
          {!isLoading && !canManageCreditCard && (
            <UtilityBanner
              flavor="error"
              prominence="quiet"
              message={
                hasCreditCard
                  ? 'Please contact your financial admin to make any changes'
                  : 'Please contact your financial admin to add a credit card'
              }
            />
          )}
        </Container>
      </Section>
      <SignupStepFooter containerSize="md">
        <Flex justifyContent="space-between">
          <Button
            variant="tertiary"
            buttonSize="medium"
            onClick={goToPreviousStep}
          >
            Back
          </Button>
          <Button
            disabled={!hasCreditCard}
            variant="primary"
            buttonSize="medium"
            onClick={handleSubmit(onFormSubmit)}
          >
            Finish setup
          </Button>
        </Flex>
      </SignupStepFooter>
      {card && showRemoveCardModal && (
        <SimpleDialog
          primaryActionFlavor="danger"
          title="Remove credit card?"
          description={`You’re about to remove card ending in ${card.credit_card_last4}.`}
          primaryActionText="Yes, remove"
          buttonsDirection="row"
          secondaryActionText="Cancel"
          onSecondaryActionClick={() => setShowRemoveCardModal(false)}
          onPrimaryActionClick={removeCard}
        />
      )}
    </>
  );
}
